import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  isSameCommentPermalink,
  updateHashInURL,
} from '@atlassian/bitkit-diff/exports/diff-permalink';

import { CommentThread } from 'src/components/conversation/src/components/comment-tree';
import { toConversationId } from 'src/components/conversation-provider/utils/to-conversation';
import { withConversationAnalytics } from 'src/components/editor/with-analytics';
import {
  Conversation,
  Props as ConversationProps,
} from 'src/containers/conversation/conversation';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { useFlag } from 'src/hooks/flag';
import { onPermalinkHashChange } from 'src/redux/pull-request/actions';
import { getContainerId } from 'src/redux/pull-request/selectors';
import history from 'src/router/history';
import { getUserAiOnboardingFlagDismissed } from 'src/sections/global/components/flags/user-ai-onboarding/user-ai-onboarding-flag';
import { getIsAIEnabledForWorkspace } from 'src/selectors/feature-selectors';
import { getRepositoryAccessLevel } from 'src/selectors/repository-selectors';
import { canModerateComments } from 'src/utils/moderate-comments';

import { AfterCommentItems } from '../../pull-request/containers/conversation/after-comment-items';

const MAX_CONVERSATION_NESTING = 1;

// We intentionally omit these props to block overrides for consistency
type Props = Omit<
  ConversationProps,
  'dataProviders' | 'objectId' | 'provider' | 'conversationsContext'
> & {
  editorPopupsMountPoint?: HTMLElement | null;
};

const BaseCommitConversation = (props: Props) => {
  const { commentThreads, editorPopupsMountPoint, ...otherProps } = props;
  const dispatch = useDispatch();

  const workspaceAiEnabled = useSelector(getIsAIEnabledForWorkspace);
  const { showFlagComponent } = useFlag({ enableForPdvs: true });

  const containerId = useSelector(getContainerId);
  const firstThread: CommentThread | undefined = commentThreads[0];
  const conversationId = firstThread
    ? toConversationId(firstThread.topComment)
    : undefined;

  const { conversationProvider, additionalConversationProviders } =
    useConversationsContext();

  const repositoryAccessLevel = useSelector(getRepositoryAccessLevel);

  const handleOnCommentPermalinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, commentId: number) => {
      event.preventDefault();
      updateHashInURL(`comment-${commentId}`, history);
      if (isSameCommentPermalink(commentId)) {
        dispatch(onPermalinkHashChange(`comment-${commentId}`));
      }
    },
    [dispatch]
  );

  return (
    <Conversation
      canModerateComments={canModerateComments(repositoryAccessLevel)}
      id={conversationId}
      maxCommentNesting={MAX_CONVERSATION_NESTING}
      onCommentPermalinkClick={handleOnCommentPermalinkClick}
      onEditorOpen={() => {
        if (!getUserAiOnboardingFlagDismissed() && workspaceAiEnabled) {
          showFlagComponent('user-ai-onboarding');
        }
      }}
      renderAfterComment={AfterCommentItems}
      {...otherProps}
      dataProviders={additionalConversationProviders}
      objectId={containerId}
      // @ts-ignore
      provider={conversationProvider}
      renderCreateTask={false}
      popupsMountPoint={editorPopupsMountPoint}
      commentThreads={commentThreads}
    />
  );
};

export const CommitConversation = withConversationAnalytics(
  React.memo(BaseCommitConversation)
);
